import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, Clock, FileText, AlertCircle } from "lucide-react";
import { api } from "@/api/client";
import SearchFilters from "@/components/search/SearchFilters";
import type { SearchFilters as SearchFiltersType, SearchResponse } from "@/types/document";
import { CONFIDENTIALITY_COLORS, STATUS_COLORS } from "@/types/document";

const EMPTY_FILTERS: SearchFiltersType = {
  jenis_pnbp_ids: [],
  siklus_ids: [],
  bentuk_ids: [],
  dimensi_ids: [],
  tag_ids: [],
  free_tags: [],
  status_ids: [],
  confidentiality: [],
};

export default function SearchPage() {
  const [keyword, setKeyword] = useState("");
  const [submittedKeyword, setSubmittedKeyword] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFiltersType>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const hasSearched = submittedKeyword !== null;

  const { data: results, isLoading, isError } = useQuery<SearchResponse>({
    queryKey: ["search", submittedKeyword, filters, page],
    queryFn: () =>
      api.post("/search/", {
        keyword: submittedKeyword || undefined,
        filters,
        page,
        page_size: 20,
        sort_by: "created_at",
        sort_order: "desc",
      }),
    enabled: hasSearched,
    retry: false,
  });

  function handleSearch(e?: React.FormEvent) {
    e?.preventDefault();
    setPage(1);
    setSubmittedKeyword(keyword);
  }

  function handleFilterChange(newFilters: SearchFiltersType) {
    setFilters(newFilters);
    setPage(1);
    if (!hasSearched) setSubmittedKeyword("");
  }

  function handleReset() {
    setFilters(EMPTY_FILTERS);
    setKeyword("");
    setSubmittedKeyword(null);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pencarian Dokumen</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Cari dengan kata kunci, filter jenis, siklus, dimensi, atau tag
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch}>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-300 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              placeholder="Ketik kata kunci pencarian... (kosongkan untuk melihat semua)"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isLoading ? "Mencari..." : "Cari"}
          </button>
        </div>
      </form>

      {/* Body: filters + results */}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Filters sidebar */}
        <SearchFilters
          value={filters}
          onChange={handleFilterChange}
          onReset={handleReset}
        />

        {/* Results area */}
        <div className="flex-1 min-w-0">
          {!hasSearched && !isLoading && (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-12 text-center">
              <Search className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">
                Gunakan kotak pencarian atau filter di sebelah kiri untuk menemukan dokumen
              </p>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          )}

          {isError && !isLoading && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Pencarian gagal</p>
                <p className="text-xs text-red-600 mt-1">
                  Terjadi kesalahan pada server. Coba ulangi pencarian atau hubungi administrator.
                </p>
              </div>
            </div>
          )}

          {hasSearched && !isLoading && !isError && results && (
            <div className="space-y-4">
              {/* Result count */}
              <div className="flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3">
                <span className="text-2xl font-bold text-blue-700">{results.total}</span>
                <div className="text-sm">
                  <p className="font-medium text-blue-800">
                    dokumen ditemukan
                    {results.keyword && (
                      <> untuk "<span className="text-blue-600 font-semibold">{results.keyword}</span>"</>
                    )}
                  </p>
                  {results.took_ms && (
                    <p className="text-blue-400 text-xs">{results.took_ms}ms</p>
                  )}
                </div>
              </div>

              {/* Result cards */}
              <div className="space-y-3">
                {results.items.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-blue-200 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <Link
                        to={`/documents/${item.id}`}
                        className="flex items-start gap-3 flex-1 min-w-0"
                      >
                        <FileText className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 hover:text-blue-600 truncate">
                            {item.judul}
                          </h3>
                          {item.nomor_dokumen && (
                            <p className="text-xs text-gray-400">{item.nomor_dokumen}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <span className="text-xs text-gray-500">
                              <span className="font-mono font-semibold text-blue-600">{item.jenis_pnbp_kode}</span>{" "}
                              {item.jenis_pnbp_nama}
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-xs text-gray-500">{item.bentuk_dokumen_nama}</span>
                            <span className="text-gray-300">•</span>
                            <span className="text-xs text-gray-500">{item.siklus_keuangan_nama}</span>
                          </div>
                        </div>
                      </Link>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[item.status_kode]}`}>
                          {item.status_nama}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${CONFIDENTIALITY_COLORS[item.tingkat_kerahasiaan]}`}>
                          {item.tingkat_kerahasiaan}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 pl-8 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(item.created_at).toLocaleDateString("id-ID")}
                      </span>
                      <span>oleh {item.created_by_username}</span>
                      {item.tanggal_berakhir && (
                        <span className="text-yellow-600">
                          Kadaluarsa: {new Date(item.tanggal_berakhir).toLocaleDateString("id-ID")}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {results.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm mt-4">
                  <p className="text-gray-500">
                    Halaman {results.page} dari {results.total_pages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-50 hover:bg-gray-50"
                    >
                      Sebelumnya
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(results.total_pages, p + 1))}
                      disabled={page === results.total_pages}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-50 hover:bg-gray-50"
                    >
                      Berikutnya
                    </button>
                  </div>
                </div>
              )}

              {results.items.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-12 text-center">
                  <p className="text-gray-400 text-sm">Tidak ada dokumen yang sesuai dengan pencarian Anda</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
